/**
 * QRCodeGenerator Component
 *
 * Generates a QR code for survey sharing with customizable size
 * and download options (PNG/SVG).
 */

import { memo, useRef, useCallback } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

// Icons
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing';

import { useDashboardTranslation } from '@/i18n/hooks/useTypedTranslation';

export type QRCodeSize = 'small' | 'medium' | 'large';

export interface QRCodeGeneratorProps {
  /** The URL to encode in the QR code */
  url: string;
  /** Size of the QR code */
  size?: QRCodeSize;
  /** Whether to include the NLPask logo in center */
  includeLogo?: boolean;
  /** Called when size is changed */
  onSizeChange?: (size: QRCodeSize) => void;
  /** Show download buttons */
  showDownloadButtons?: boolean;
  /** Survey title for filename */
  surveyTitle?: string;
}

// More reasonable sizes for modal display
const SIZE_MAP: Record<QRCodeSize, number> = {
  small: 140,
  medium: 180,
  large: 220,
};

export const QRCodeGenerator = memo(function QRCodeGenerator({
  url,
  size = 'medium',
  includeLogo = false,
  onSizeChange,
  showDownloadButtons = true,
  surveyTitle = 'survey',
}: QRCodeGeneratorProps) {
  const { t } = useDashboardTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<HTMLDivElement>(null);

  const pixelSize = SIZE_MAP[size];

  // Generate a safe filename from survey title
  const safeFilename = surveyTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50) || 'survey';

  /**
   * Downloads the QR code as PNG.
   */
  const downloadAsPng = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `${safeFilename}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [safeFilename]);

  /**
   * Downloads the QR code as SVG.
   */
  const downloadAsSvg = useCallback(() => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.download = `${safeFilename}-qr.svg`;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  }, [safeFilename]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
      {/* QR Code Card with branded frame */}
      <Box
        sx={{
          position: 'relative',
          p: 2,
          bgcolor: 'white',
          borderRadius: 3,
          border: 2,
          borderColor: alpha('#14B8A6', 0.2),
          boxShadow: '0 4px 20px rgba(20, 184, 166, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* QR Code */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'white',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* SVG version (for display and SVG download) */}
          <Box ref={svgRef} sx={{ display: 'block', lineHeight: 0 }}>
            <QRCodeSVG
              value={url}
              size={pixelSize}
              level="M"
              marginSize={1}
              fgColor="#1a1a2e"
              imageSettings={
                includeLogo
                  ? {
                      src: '/logo-icon.png',
                      height: pixelSize * 0.2,
                      width: pixelSize * 0.2,
                      excavate: true,
                    }
                  : undefined
              }
            />
          </Box>

          {/* Hidden canvas version (for PNG download) */}
          <Box ref={canvasRef} sx={{ display: 'none' }}>
            <QRCodeCanvas
              value={url}
              size={pixelSize * 2} // Higher resolution for downloads
              level="M"
              marginSize={1}
              fgColor="#1a1a2e"
              imageSettings={
                includeLogo
                  ? {
                      src: '/logo-icon.png',
                      height: pixelSize * 0.4,
                      width: pixelSize * 0.4,
                      excavate: true,
                    }
                  : undefined
              }
            />
          </Box>
        </Box>

        {/* Scan instruction */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          <PhonelinkRingIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {t('share.scanToOpen', 'Scan to open survey')}
          </Typography>
        </Box>
      </Box>

      {/* Controls row */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}>
        {/* Size selector */}
        {onSizeChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('share.size', 'Size')}:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button
                onClick={() => onSizeChange('small')}
                variant={size === 'small' ? 'contained' : 'outlined'}
                sx={{ minWidth: 36, px: 1 }}
              >
                S
              </Button>
              <Button
                onClick={() => onSizeChange('medium')}
                variant={size === 'medium' ? 'contained' : 'outlined'}
                sx={{ minWidth: 36, px: 1 }}
              >
                M
              </Button>
              <Button
                onClick={() => onSizeChange('large')}
                variant={size === 'large' ? 'contained' : 'outlined'}
                sx={{ minWidth: 36, px: 1 }}
              >
                L
              </Button>
            </ButtonGroup>
          </Box>
        )}

        {/* Download buttons */}
        {showDownloadButtons && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageIcon sx={{ fontSize: 16 }} />}
              onClick={downloadAsPng}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              PNG
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={downloadAsSvg}
              sx={{ fontSize: '0.75rem', py: 0.5 }}
            >
              SVG
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default QRCodeGenerator;
